/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { R } from 'retemplate';
import { Equatable, Equaler, Comparable } from '@esfx/equatable';
import { isInt, compareNumbers, CIVILDATE_HASH_SEED } from './internal/core';
import { getDaysInMonth, getDate, getDatePart, getWeeksInYear, getCalendarDateFromWeekDate, getDaysInYear, getCalendarDateFromOrdinalDate, DatePart } from './internal/date';
import { dateDiff, datePlus } from './internal/math';
import { DATE } from './internal/symbols';
import { EPOCH_DAYS } from './internal/types';
import { DATE_EXTENDED, DATE_BASIC } from './internal/patterns';
import { D2, D3, D4 } from './internal/formats';
import { Temporal, Components, DateDelta, DateUnit } from './types';
import { Clock } from './Clock';
import { CivilTime } from './CivilTime';
import { CivilDateTime } from './CivilDateTime';
import { Duration } from './Duration';

const dateExtendedRegExp = new RegExp(R`^${DATE_EXTENDED}$`);
const dateBasicRegExp = new RegExp(R`^${DATE_BASIC}$`);

export class CivilDate implements Temporal<CivilDate>, Equatable, Comparable {
    /* @internal */ private [DATE]: EPOCH_DAYS; // days since epoch

    constructor(year: number, month: number, day: number) {
        if (!isInt(year)) throw new RangeError(`The value ${year} is not a valid year.`);
        if (!isInt(month) || month < 1 || month > 12) throw new RangeError(`The value ${month} is not a valid month.`);
        if (!isInt(day) || day < 1 || day > getDaysInMonth(year, month)) throw new RangeError(`The value ${day} is not a valid day.`);
        this[DATE] = getDate(year, month, day);
    }

    get year() { return getDatePart(this[DATE], DatePart.year); }
    get month() { return getDatePart(this[DATE], DatePart.month); }
    get day() { return getDatePart(this[DATE], DatePart.day); }
    get dayOfYear() { return getDatePart(this[DATE], DatePart.dayOfYear); }
    get dayOfWeek() { return getDatePart(this[DATE], DatePart.dayOfWeek); }
    get weekOfYear() { return getDatePart(this[DATE], DatePart.weekOfYear); }

    plus(deltas: Components<DateDelta> = {}) {
        const { years = 0, months = 0, days = 0 } = deltas;
        if (!isInt(years)) throw new RangeError(`The value ${years} is not valid for 'years' as it is not an integer.`);
        if (!isInt(months)) throw new RangeError(`The value ${months} is not valid for 'months' as it is not an integer.`);
        if (!isInt(days)) throw new RangeError(`The value ${days} is not valid for 'days' as it is not an integer.`);
        return CivilDate.fromDayOfEpoch(datePlus(this[DATE], years, months, days));
    }

    minus(deltas: Components<DateDelta> = {}) {
        const { years = 0, months = 0, days = 0 } = deltas;
        if (!isInt(years)) throw new RangeError(`The value ${years} is not valid for 'years' as it is not an integer.`);
        if (!isInt(months)) throw new RangeError(`The value ${months} is not valid for 'months' as it is not an integer.`);
        if (!isInt(days)) throw new RangeError(`The value ${days} is not valid for 'days' as it is not an integer.`);
        return CivilDate.fromDayOfEpoch(datePlus(this[DATE], -years, -months, -days));
    }

    with(components: Components<DateUnit> = {}) {
        const { year = this.year, month = this.month, day = this.day } = components;
        return new CivilDate(year, month, day);
    }

    withTime(time: CivilTime) {
        if (!(time instanceof CivilTime)) throw new TypeError("CivilTime expected: time");
        return CivilDateTime.from(this, time);
    }

    until(other: CivilDate) {
        if (!(other instanceof CivilDate)) throw new TypeError("CivilDate expected: other");
        return new Duration(dateDiff(this[DATE], other[DATE]));
    }

    since(other: CivilDate) {
        if (!(other instanceof CivilDate)) throw new TypeError("CivilDate expected: other");
        return other.until(this);
    }

    toCalendarDateString() {
        return `${D4(this.year)}-${D2(this.month)}-${D2(this.day)}`;
    }

    toWeekDateString() {
        let { year, weekOfYear } = this;
        while (weekOfYear < 1) year--, weekOfYear += getWeeksInYear(year);
        while (weekOfYear > getWeeksInYear(year)) weekOfYear -= getWeeksInYear(year), year++;
        return `${D4(year)}-W${D2(weekOfYear)}-${this.dayOfWeek}`;
    }

    toOrdinalDateString() {
        return `${D4(this.year)}-${D3(this.dayOfYear)}`;
    }

    toString() {
        return this.toCalendarDateString();
    }

    toISOString() {
        return this.toCalendarDateString();
    }

    toJSON() {
        return this.toCalendarDateString();
    }

    equals(other: CivilDate) {
        return other !== null
            && other !== undefined
            && this[DATE] === other[DATE];
    }

    compareTo(other: CivilDate): number {
        return compareNumbers(this[DATE], other[DATE]);
    }

    static fromDayOfEpoch(day: number): CivilDate {
        if (!isInt(day)) throw new RangeError(`The value ${day} is not valid for the number of days.`);
        return Object.create(CivilDate.prototype, {
            [DATE]: { value: day }
        });
    }

    static fromCalendarDate(year: number, month: number, day: number) {
        return new CivilDate(year, month, day);
    }

    static fromWeekDate(year: number, weekOfYear: number, dayOfWeek: number) {
        if (!isInt(year)) throw new RangeError(`The value ${year} is not a valid year.`);
        if (!isInt(weekOfYear) || weekOfYear < 1 || weekOfYear > getWeeksInYear(year)) throw new RangeError(`The value ${weekOfYear} is not a valid week for the year ${year}.`);
        if (!isInt(dayOfWeek) || dayOfWeek < 1 || dayOfWeek > 7) throw new RangeError(`The value ${dayOfWeek} is not a valid day of the week.`);

        const components = getCalendarDateFromWeekDate(year, weekOfYear, dayOfWeek);
        return CivilDate.fromCalendarDate(components.year, components.month, components.day);
    }

    static fromOrdinalDate(year: number, dayOfYear: number) {
        if (!isInt(year)) throw new RangeError(`The value ${year} is not a valid year.`);
        if (!isInt(dayOfYear) || dayOfYear < 1 || dayOfYear > getDaysInYear(year)) throw new RangeError(`The value ${dayOfYear} is not a valid day for the year ${year}.`);

        const components = getCalendarDateFromOrdinalDate(year, dayOfYear);
        return CivilDate.fromCalendarDate(components.year, components.month, components.day);
    }

    static fromString(text: string) {
        const match = dateExtendedRegExp.exec(text) || dateBasicRegExp.exec(text);
        if (match) {
            const { year, month, day, weekOfYear, dayOfWeek, dayOfYear } = match.groups!;
            if (year && month && day) return CivilDate.fromCalendarDate(+year, +month, +day);
            if (year && weekOfYear && dayOfWeek) return CivilDate.fromWeekDate(+year, +weekOfYear, + dayOfWeek);
            if (year && dayOfYear) return CivilDate.fromOrdinalDate(+year, + dayOfYear);
        }

        throw new SyntaxError(`Invalid date string '${text}'.`);
    }

    static now(clock: Clock) {
        return clock.currentInstant().withZone(clock.zone).toCivilDate();
    }

    [Equatable.equals](other: unknown) {
        return other instanceof CivilDate
            && this.equals(other);
    }

    [Equatable.hash]() {
        let hc = CIVILDATE_HASH_SEED;
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this[DATE]);
        return hc;
    }

    [Comparable.compareTo](other: unknown) {
        if (!(other instanceof CivilDate)) throw new TypeError();
        return this.compareTo(other);
    }
}

Object.defineProperty(CivilDate.prototype, Symbol.toStringTag, {
    writable: true,
    configurable: true,
    value: "CivilDate"
});